// src/components/student/TrendChart.jsx
// Inline SVG line + gradient-area chart. No chart dependency.
// Props: data [{ label, value }], unit ("%" | "XP"), color (hex).
import React, { useId } from "react";
import { motion } from "framer-motion";

const W = 640;   // viewBox width  (scales to container via width:100%)
const H = 200;   // viewBox height
const PAD = { top: 16, right: 12, bottom: 24, left: 32 };

export default function TrendChart({ data = [], unit = "%", color = "#6366f1" }) {
  const gid = useId().replace(/:/g, "");

  if (!data.length) {
    return (
      <div className="h-40 flex flex-col items-center justify-center text-center text-gray-400">
        <span className="text-3xl mb-2">📈</span>
        <p className="text-sm font-semibold">No quizzes yet</p>
        <p className="text-xs">Take a few quizzes and your trend will appear here.</p>
      </div>
    );
  }

  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const maxRaw = Math.max(...data.map((d) => d.value), unit === "%" ? 100 : 1);
  const max = unit === "%" ? 100 : Math.ceil(maxRaw / 10) * 10 || 1;
  const n = data.length;

  // X positions: single point sits centered.
  const x = (i) => (n === 1 ? PAD.left + innerW / 2 : PAD.left + (i / (n - 1)) * innerW);
  const y = (v) => PAD.top + innerH - (Math.min(v, max) / max) * innerH;

  const points = data.map((d, i) => ({ ...d, cx: x(i), cy: y(d.value) }));
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.cx.toFixed(1)} ${p.cy.toFixed(1)}`).join(" ");
  const areaPath =
    `M ${points[0].cx.toFixed(1)} ${(PAD.top + innerH).toFixed(1)} ` +
    points.map((p) => `L ${p.cx.toFixed(1)} ${p.cy.toFixed(1)}`).join(" ") +
    ` L ${points[n - 1].cx.toFixed(1)} ${(PAD.top + innerH).toFixed(1)} Z`;

  // Gridlines / y-axis ticks (0, mid, max)
  const ticks = [0, max / 2, max];

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none" role="img">
        <defs>
          <linearGradient id={`grad-${gid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Gridlines + y labels */}
        {ticks.map((t, i) => {
          const gy = y(t);
          return (
            <g key={i}>
              <line x1={PAD.left} y1={gy} x2={W - PAD.right} y2={gy} stroke="#eef2f7" strokeWidth="1" />
              <text x={PAD.left - 6} y={gy + 3} textAnchor="end" fontSize="10" fill="#9ca3af">
                {Math.round(t)}{unit === "%" ? "" : ""}
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        {n > 1 && (
          <motion.path
            d={areaPath}
            fill={`url(#grad-${gid})`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          />
        )}

        {/* Line */}
        {n > 1 && (
          <motion.path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          />
        )}

        {/* Dots */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.cx} cy={p.cy} r={n > 30 ? 2 : 3.5} fill="#fff" stroke={color} strokeWidth="2" />
            <title>{`${p.label}: ${p.value}${unit === "%" ? "%" : " XP"}`}</title>
          </g>
        ))}
      </svg>

      {n === 1 && (
        <p className="text-xs text-gray-400 text-center mt-1">Take more quizzes to see a trend line.</p>
      )}
    </div>
  );
}
